/*
 * dry moves: lighter background videos on phones.
 *
 * snippets/video-with-alt.liquid renders videos without controls with
 * <source data-dry-src="…mp4"> and data-dry-hls="…m3u8" instead of a playable
 * source. This script sets the source only for videos that are displayed at
 * the current breakpoint (the hidden desktop or mobile copy is never loaded):
 * - phones (≤ 767px) with native HLS: the smallest rendition of the HLS stream
 *   that still covers the video's displayed size at up to 2x pixel density
 *   (e.g. 480p for small product card videos, 1080p for a full-screen hero),
 *   instead of always the 1080p MP4,
 * - everything else: the MP4, as before.
 * Aurora's lazy-video component still decides when lazy videos start loading;
 * banner (eager autoplay) videos wait for the page's load event.
 * Product gallery videos (Aurora's deferred-media) get their source written
 * into their <template> in advance, because Aurora clones and plays them
 * right away on a tap.
 */
(() => {
  const MOBILE_QUERY = window.matchMedia('(max-width: 767px)');
  const HLS_TYPE = 'application/vnd.apple.mpegurl';
  const VIDEO_SELECTOR = 'video[data-dry-video]';
  const CAN_PLAY_HLS = document.createElement('video').canPlayType(HLS_TYPE) !== '';
  const variantCache = new Map();

  // Device pixels per CSS pixel the chosen rendition has to cover. Capped at 2:
  // the jump from 2x to 3x is barely visible in a moving video but costs a lot
  // of data.
  const MAX_DENSITY = 2;

  // Resolves on the window load event, or after LOAD_TIMEOUT at the latest
  // (slow third-party scripts can hold the load event back for a long time).
  const LOAD_TIMEOUT = 5000;
  const pageLoaded = new Promise(resolve => {
    if (document.readyState === 'complete') {
      resolve();
      return;
    }
    window.addEventListener('load', resolve, { once: true });
    setTimeout(resolve, LOAD_TIMEOUT);
  });

  const prefersLessData = () => {
    const connection = navigator.connection;
    return !!connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || ''));
  };

  const variantsOf = (masterUrl) => {
    if (!variantCache.has(masterUrl)) {
      const request = fetch(masterUrl)
        .then(response => (response.ok ? response.text() : Promise.reject(response.status)))
        .then(playlist => {
          const lines = playlist.split('\n');
          const variants = [];
          lines.forEach((line, index) => {
            if (!line.startsWith('#EXT-X-STREAM-INF')) {
              return;
            }
            const uri = (lines[index + 1] || '').trim();
            const bandwidth = /BANDWIDTH=(\d+)/.exec(line);
            const resolution = /RESOLUTION=(\d+)x(\d+)/.exec(line);
            if (uri && !uri.startsWith('#')) {
              variants.push({
                url: new URL(uri, masterUrl).href,
                bandwidth: bandwidth ? Number(bandwidth[1]) : Infinity,
                width: resolution ? Number(resolution[1]) : 0,
                height: resolution ? Number(resolution[2]) : 0,
              });
            }
          });
          return variants.sort((a, b) => a.bandwidth - b.bandwidth);
        })
        .catch(() => []);
      variantCache.set(masterUrl, request);
    }
    return variantCache.get(masterUrl);
  };

  // The video is shown with object-fit: cover, i.e. scaled by
  // max(boxWidth / width, boxHeight / height). A rendition is sharp enough when
  // that scale stays at or below 1 / density, so it has to cover both the
  // displayed width and height at the target density. If none does, take the
  // largest one.
  const pickVariant = (variants, element) => {
    if (!variants.length) {
      return null;
    }
    const box = element.getBoundingClientRect();
    const density = Math.min(window.devicePixelRatio || 1, MAX_DENSITY);
    const sharp = variants.find(v => v.width >= box.width * density && v.height >= box.height * density);
    return (sharp || variants[variants.length - 1]).url;
  };

  const isDisplayed = video => video.getClientRects().length > 0;

  const setSource = (source, url, type) => {
    source.setAttribute('src', url);
    source.setAttribute('type', type);
  };

  // Safari shows a black frame between the poster and the first video frame
  // when a video starts (most visible with HLS on iPhones). To hide it, an
  // image is laid over the video until the first frame has really been
  // painted:
  // - gallery videos (deferred-media): a copy of Aurora's own poster image,
  //   which Aurora hides as soon as the video is added (the video's poster is
  //   only 100px wide, and its background is black),
  // - banner (eager autoplay) videos: the poster image that
  //   snippets/video-with-alt.liquid renders right after the video. It is
  //   part of the HTML, so it is painted with the page; an image added here
  //   later would become a late LCP element.
  // If playback is blocked (e.g. low power mode) the image simply stays.
  const coverWithPoster = video => {
    if (video.dataset.dryCovered) {
      return;
    }
    const deferred = video.closest('deferred-media');
    const themePoster = deferred && deferred.querySelector('.deferred-media__poster img');
    // cover: the element removed at the end; fade: the image that fades out
    // (the rendered cover is a <picture>, see snippets/video-with-alt.liquid).
    let cover;
    let fade;
    const renderedCover = video.nextElementSibling;
    if (renderedCover && renderedCover.hasAttribute('data-dry-video-cover')) {
      cover = renderedCover;
      fade = renderedCover.querySelector('img') || renderedCover;
    } else if (themePoster) {
      const style = getComputedStyle(video);
      cover = themePoster.cloneNode();
      cover.removeAttribute('class');
      cover.loading = 'eager';
      Object.assign(cover.style, {
        position: 'absolute',
        inset: '0',
        width: '100%',
        height: '100%',
        objectFit: style.objectFit,
        backgroundColor: style.backgroundColor,
      });
      // deferred-media is an inline element; the box the video fills is its
      // parent (the gallery slide's media wrapper)
      if (getComputedStyle(deferred.parentElement).position === 'static') {
        deferred.parentElement.style.position = 'relative';
      }
      cover.alt = '';
      cover.setAttribute('aria-hidden', 'true');
      cover.style.pointerEvents = 'none';
      video.after(cover);
      fade = cover;
    } else {
      return;
    }
    video.dataset.dryCovered = 'true';
    fade.style.transition = 'opacity 0.25s ease-out';

    let revealed = false;
    const reveal = () => {
      if (revealed) {
        return;
      }
      revealed = true;
      video.removeEventListener('timeupdate', onTime);
      fade.style.opacity = '0';
      setTimeout(() => cover.remove(), 300);
    };
    // Whichever comes first: the first painted frame, or playback time moving
    // on (requestVideoFrameCallback does not fire while the page is hidden).
    const onTime = () => {
      if (video.currentTime > 0) {
        reveal();
      }
    };
    video.addEventListener('playing', () => {
      if (video.requestVideoFrameCallback) {
        video.requestVideoFrameCallback(() => reveal());
      }
      video.addEventListener('timeupdate', onTime);
    }, { once: true });
  };

  // playRequested: something (Aurora's deferred-media) already called play()
  // before the source was set; load() cancels that call, so play again.
  const start = (video, playRequested) => {
    const willPlay = video.autoplay || playRequested;
    if (video.dataset.dryPoster && !video.poster) {
      video.poster = video.dataset.dryPoster;
    }
    if (willPlay) {
      coverWithPoster(video);
    }
    video.load();
    if (willPlay) {
      video.play().catch(() => {});
    }
  };

  // Writes the source into a gallery video's <template> before Aurora clones
  // it, so its play() call on a tap finds a playable video. The template's
  // video has no size yet, so the media wrapper it will fill is measured.
  const prepareDeferred = async deferred => {
    const template = deferred.querySelector(':scope > template');
    const video = template && template.content.querySelector(VIDEO_SELECTOR);
    if (!video || deferred.dataset.dryPrepared || !isDisplayed(deferred)) {
      return;
    }
    const source = video.querySelector('source[data-dry-src]');
    if (!source) {
      return;
    }
    deferred.dataset.dryPrepared = 'true';
    const hls = video.dataset.dryHls;
    const wantsHls = MOBILE_QUERY.matches && hls && CAN_PLAY_HLS;
    const variant = wantsHls
      ? pickVariant(await variantsOf(new URL(hls, window.location.href).href), deferred.parentElement)
      : null;
    if (variant) {
      setSource(source, variant, HLS_TYPE);
    } else {
      setSource(source, source.dataset.drySrc, 'video/mp4');
    }
    video.dataset.drySourceSet = 'true';
  };

  const applySource = async video => {
    if (video.dataset.drySourceSet || !isDisplayed(video)) {
      return;
    }
    const playRequested = !video.paused;
    const source = video.querySelector('source[data-dry-src]');
    if (!source) {
      return;
    }
    video.dataset.drySourceSet = 'true';

    const isLazy = video.getAttribute('preload') === 'none';
    // Product card videos that are not in the first slide of the card gallery
    // only show their poster on phones: Aurora would otherwise preload several
    // MB for a slide that is only seen after swiping (the card links to the
    // product page anyway).
    // The same applies to card videos that are not the card's main media,
    // e.g. the hover media in product recommendations (there is no hover on
    // phones).
    const slide = video.closest('.product-card .carousel__slide');
    const isLaterCardSlide = !!slide && slide.parentElement.firstElementChild !== slide;
    const isSecondaryCardMedia = !!video.closest('.product-card') && !video.hasAttribute('data-main-media');
    if (MOBILE_QUERY.matches && isLazy && (isLaterCardSlide || isSecondaryCardMedia)) {
      delete video.dataset.drySourceSet;
      return;
    }

    // Banner videos (eager autoplay) only start loading once the page has
    // loaded, so their several MB don't compete with the page itself (and
    // Lighthouse doesn't count them towards LCP). Until then, and for visitors
    // who save data or are on a very slow connection, the poster is shown.
    if (!isLazy && video.autoplay && !playRequested) {
      if (prefersLessData()) {
        return;
      }
      await pageLoaded;
    }

    const mp4 = source.dataset.drySrc;
    const hls = video.dataset.dryHls;
    const wantsHls = MOBILE_QUERY.matches && hls && video.canPlayType(HLS_TYPE) !== '';

    if (!wantsHls) {
      setSource(source, mp4, 'video/mp4');
      if (!isLazy) {
        start(video, playRequested);
      }
      return;
    }

    // Lazy videos get the MP4 right away as a fallback, in case Aurora starts
    // loading them before the playlist has been read (preload="none" keeps
    // the browser from downloading it on its own).
    if (isLazy) {
      setSource(source, mp4, 'video/mp4');
    }
    const variant = pickVariant(await variantsOf(new URL(hls, window.location.href).href), video);
    // Only restart videos that Aurora has really started loading meanwhile; a
    // lazy video that was never requested must stay untouched.
    const alreadyLoading = video.readyState > HTMLMediaElement.HAVE_NOTHING
      || video.networkState === HTMLMediaElement.NETWORK_LOADING;
    if (variant) {
      setSource(source, variant, HLS_TYPE);
    } else if (!isLazy) {
      setSource(source, mp4, 'video/mp4');
    }
    if (!isLazy || alreadyLoading) {
      start(video, playRequested);
    }
  };

  // Lazy videos (snippets/video-with-alt.liquid renders their poster as
  // data-dry-poster) get their poster once they come near the viewport; copies
  // hidden at the current breakpoint never do. Banner videos with a cover get
  // it when they start (see start).
  const posterObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.poster = entry.target.dataset.dryPoster;
          posterObserver.unobserve(entry.target);
        }
      });
    }, { rootMargin: '300px' })
    : null;
  const deferPoster = video => {
    const cover = video.nextElementSibling;
    if (!video.dataset.dryPoster || video.poster || (cover && cover.hasAttribute('data-dry-video-cover'))) {
      return;
    }
    if (posterObserver) {
      posterObserver.observe(video);
    } else {
      video.poster = video.dataset.dryPoster;
    }
  };

  // A gallery video that Aurora has just added from its prepared template is
  // already starting to play; it only needs the cover.
  const handleAdded = video => {
    if (video.closest('deferred-media') && !video.paused) {
      coverWithPoster(video);
    }
    deferPoster(video);
    applySource(video);
  };

  const scan = (root = document) => {
    root.querySelectorAll('deferred-media').forEach(prepareDeferred);
    root.querySelectorAll(VIDEO_SELECTOR).forEach(video => {
      deferPoster(video);
      applySource(video);
    });
  };

  const init = () => {
    scan();
    // Videos that only become visible at another breakpoint
    MOBILE_QUERY.addEventListener('change', () => scan());
    let resizeFrame = null;
    window.addEventListener('resize', () => {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => scan());
    }, { passive: true });
    // Videos added later (quick view, cart drawer, theme editor, section rendering)
    new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType !== 1) {
            return;
          }
          if (node.matches(VIDEO_SELECTOR)) {
            handleAdded(node);
          } else if (node.matches('deferred-media')) {
            prepareDeferred(node);
          } else if (node.querySelector('deferred-media, ' + VIDEO_SELECTOR)) {
            scan(node);
          }
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
